import { useMemo, useState } from 'react'
import { Button, Checkbox, Select, StatusChip } from '../Common'
import { QuickDelegateSheet } from '../FieldPass/QuickDelegateSheet'
import { useNls } from '../../nls/nlsProvider'
import { formatRoleLabel } from '../../utils/roleLabels'
import type { FieldPassesModel } from './useFieldPassesModel'

const toMs = (value: unknown): number | null => {
  if (!value || typeof value !== 'object' || typeof (value as { toMillis?: unknown }).toMillis !== 'function') return null
  return (value as { toMillis: () => number }).toMillis()
}

const formatDate = (value: unknown): string => {
  const ms = toMs(value)
  if (!ms) return 'Not set'
  return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

type Props = { model: FieldPassesModel }

/**
 * Right-panel content for the Field Passes tab. Renders either the
 * create-a-pass multi-select form (default) or the details of a selected
 * pass. State is fully owned by useFieldPassesModel — this component is
 * a thin renderer.
 */
export const FieldPassesSidecar = ({ model }: Props) => {
  const { t } = useNls()
  const [quickTargetId, setQuickTargetId] = useState('')
  const [quickOpen, setQuickOpen] = useState(false)
  const quickTarget = useMemo(
    () => model.options.find((option) => option.id === quickTargetId) ?? null,
    [model.options, quickTargetId],
  )

  if (!model.isCreating && !model.selectedPass) {
    // Fallback — nothing selected and not creating: prompt to create.
    return (
      <div className="invitesSidecarPremium">
        <section className="invitesSidecarHero">
          <header className="invitesSidecarHeader">
            <div className="invitesSidecarAvatar"><span>+</span></div>
            <div className="invitesSidecarIdentity">
              <strong>Field Passes</strong>
              <span>Pick a pass on the left, or create a new burner.</span>
            </div>
          </header>
          <div className="invitesSidecarActions">
            <Button variant="primary" onClick={() => model.startCreate()}>+ Create Field Pass</Button>
          </div>
        </section>
      </div>
    )
  }

  if (model.isCreating) {
    return (
      <div className="invitesSidecarPremium">
        <section className="invitesSidecarHero">
          <header className="invitesSidecarHeader">
            <div className="invitesSidecarAvatar"><span>+</span></div>
            <div className="invitesSidecarIdentity">
              <strong>Create Field Pass</strong>
              <span>Temporary field access. Free: 1/day. Basic: 10/day. Unlimited: unlimited.</span>
            </div>
          </header>
        </section>
        {model.error ? <div className="invitesSidecarBanner isError">{model.error}</div> : null}
        <section className="invitesSidecarSection">
          <h4>Quick single-item</h4>
          <Select<string>
            trigger="labeledPill"
            label="Task"
            placeholder="Choose task"
            value={quickTargetId || null}
            ariaLabel="Quick Field Pass task"
            searchable
            options={model.options.map((option) => ({
              value: option.id,
              label: option.title,
              searchText: `${option.title} ${option.workflowStatus}`,
            }))}
            onChange={(val) => setQuickTargetId(val ?? '')}
          />
          <Button variant="secondary" size="sm" disabled={!quickTarget} onClick={() => setQuickOpen(true)}>
            Quick single-item
          </Button>
        </section>
        <section className="invitesSidecarSection">
          <h4>Assigned checklist items</h4>
          <div className="invitesSidecarChecklistList">
            {model.options.length === 0 ? <p className="text-xs text-[var(--muted)]">No helper-visible checklist tasks found.</p> : null}
            {model.options.map((option) => (
              <Checkbox
                key={option.id}
                wrapperClassName="invitesSidecarChecklistRow"
                checked={model.selectedIds.has(option.id)}
                onChange={() => model.toggleTarget(option.id)}
                label={
                  <>
                    <span className="invitesSidecarChecklistRow__title">{option.title}</span>
                    <span className="invitesSidecarChecklistRow__status">{option.workflowStatus}</span>
                  </>
                }
              />
            ))}
          </div>
          <Checkbox
            wrapperClassName="invitesSidecarRevokeToggle"
            checked={model.deleteOnCompletion}
            onChange={(next) => model.setDeleteOnCompletion(next)}
            label="Revoke automatically when all assigned work is complete"
          />
        </section>
        <div className="invitesSidecarActions isFooter">
          <Button variant="primary" disabled={model.busy === 'create' || model.selectedIds.size === 0} onClick={() => void model.create()}>
            Create Field Pass
          </Button>
          {model.lastLink ? (
            <Button variant="secondary" onClick={() => void model.copyText(model.lastLink!).then(() => model.setStatus('Field Pass link copied.'))}>
              Copy Last Link
            </Button>
          ) : null}
        </div>
        {model.lastLink ? (
          <code className="invitesSidecarLinkPill">{model.lastLink}</code>
        ) : null}
        <QuickDelegateSheet
          open={quickOpen}
          workspaceId={model.workspaceId}
          targets={quickTarget ? [{ id: quickTarget.id, title: quickTarget.title }] : []}
          onClose={() => setQuickOpen(false)}
          onNotice={(message, tone) => tone === 'error' ? model.setError(message) : model.setStatus(message)}
          onCreated={(result, url) => model.registerCreatedPass(result.pass, url, { stayInCreate: true })}
        />
      </div>
    )
  }

  const pass = model.selectedPass!
  return (
    <div className="invitesSidecarPremium">
      <section className="invitesSidecarHero">
        <header className="invitesSidecarHeader">
          <div className="invitesSidecarAvatar"><span>{(pass.displayName || 'F').charAt(0).toUpperCase()}</span></div>
          <div className="invitesSidecarIdentity">
            <strong>{pass.displayName ? `Field Pass · ${pass.displayName}` : 'Unredeemed Field Pass'}</strong>
            <span>Expires {formatDate(pass.expiresAt)}</span>
            <div className="invitesSidecarChips">
              <StatusChip
                size="sm"
                tone={pass.status === 'ACTIVE' || pass.status === 'REDEEMED' ? 'success' : 'muted'}
              >
                {pass.status}
              </StatusChip>
            </div>
          </div>
        </header>
        <div className="invitesSidecarActions">
          <Button variant="tertiary" size="sm" onClick={() => model.startCreate()}>+ New</Button>
          <Button
            variant="tertiary"
            size="sm"
            disabled={model.busy === `revoke:${pass.id}` || pass.status === 'REVOKED'}
            onClick={() => void model.revoke(pass)}
          >
            Revoke
          </Button>
        </div>
      </section>
      <section className="invitesSidecarSection">
        <h4>Targets</h4>
        <ul className="grid gap-1 text-xs text-[var(--text)]">
          {pass.targets.map((target) => (<li key={target.id}>{target.title}</li>))}
        </ul>
      </section>
      {pass.redeemedSessionId ? (
        <section className="invitesSidecarSection">
          <h4>Add member to Field Pass chat</h4>
          <label className="invitesSidecarControlLabel">
            Choose member
            <Select
              trigger="pill"
              ariaLabel="Choose member"
              placeholder="Choose member"
              value={model.memberSelection[pass.id] || null}
              onChange={(val) => model.setMemberSelection((current) => ({ ...current, [pass.id]: val }))}
              options={model.members.map((member) => ({
                value: member.uid,
                label: `${member.name} · ${formatRoleLabel(t, member.role)}`,
              }))}
            />
          </label>
          <Button
            variant="secondary"
            size="sm"
            disabled={model.busy === `member:${pass.id}` || !model.memberSelection[pass.id]}
            onClick={() => void model.addMemberToChat(pass)}
          >
            Add to Field Pass Chat
          </Button>
        </section>
      ) : null}
    </div>
  )
}

export default FieldPassesSidecar
